import { type Theme, styled, useTheme } from '@mui/material/styles';
import MuiAppBar from '@mui/material/AppBar';
import { type AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import { Avatar } from '@/components/Avatar.tsx';
import Tooltip from '@mui/material/Tooltip';
import type React from 'react';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Typography, useMediaQuery } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { RightDrawer } from '@/components/Layout/RightDrawer.tsx';
import { useFetchUser } from '@/api/user.ts';
import { useTranslation } from 'react-i18next';
import { closedDrawerWidth, openDrawerWidth, topBarHeight } from './constants';

interface AppBarProps extends MuiAppBarProps {
  open: boolean;
  isMobile: boolean;
}

const StyledAppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile',
})<AppBarProps>(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open, isMobile }) => open && isMobile,
      style: {
        marginLeft: closedDrawerWidth,
        width: `calc(100% - ${closedDrawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
    {
      props: ({ open, isMobile }) => open && !isMobile,
      style: {
        marginLeft: openDrawerWidth,
        width: `calc(100% - ${openDrawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

interface TopBarProps {
  openLeftDrawer: boolean;
  handleOpenLeftDrawer: () => void;
  handleOpenRightDrawer: (event: React.MouseEvent<HTMLElement>) => void;
  anchorElUser: HTMLElement | null;
  handleCloseRightDrawer: () => void;
}

const topBarIconBoxSx = [
  (theme: Theme) => ({
    width: 34,
    height: 34,
    mb: 0.7,
    borderRadius: theme.borderRadii.br12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: 'grey.300',
  }),
  (theme: Theme) =>
    theme.applyStyles('dark', {
      backgroundColor: theme.vars?.palette.background.default,
      borderColor: theme.vars?.palette.grey[900],
      borderWidth: 0.5,
    }),
] as const;

export const TopBar = ({
  openLeftDrawer,
  handleOpenLeftDrawer,
  handleOpenRightDrawer,
  anchorElUser,
  handleCloseRightDrawer,
}: TopBarProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const { data, isPending } = useFetchUser();
  const username =
    isPending || !data ? undefined : data.clientPrincipal.claims.find((x) => x.typ === 'name')?.val;

  return (
    <StyledAppBar
      position="fixed"
      open={openLeftDrawer}
      isMobile={isMobile}
      color="transparent"
      elevation={0}
      sx={(theme) => ({
        backdropFilter: 'blur(20px)',
        height: topBarHeight,
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          right: 0,
          left: 0,
          height: '1px',
          backgroundColor: theme.vars?.palette.divider,
        },
        ...theme.applyStyles('light', {
          filter: 'brightness(1.04)',
        }),
        ...theme.applyStyles('dark', {
          backgroundColor: theme.vars?.palette.shades.neutral,
          '&::after': {
            height: '0.5px',
          },
        }),
      })}
    >
      <Toolbar sx={{ height: 57 }}>
        {isMobile && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleOpenLeftDrawer}
            edge="start"
            sx={{
              marginRight: 5,
              '&:hover': { background: 'none' },
              ...(openLeftDrawer && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Box
          sx={[
            ...topBarIconBoxSx,
            {
              mr: 1,
              ml: 'auto',
              width: 'auto',
              padding: '0 16px',
              backgroundColor: theme.vars?.palette.satosRed[50],
            },
            (theme) =>
              theme.applyStyles('dark', {
                backgroundColor: theme.vars?.palette.background.default,
              }),
          ]}
        >
          <Typography
            variant="body1"
            sx={(theme) => ({
              color: 'text.secondary',
              fontSize: theme.fontSizes.f12,
              fontWeight: 500,
              ...theme.applyStyles('dark', {
                color: 'text.primary',
              }),
            })}
          >
            {username
              ? `👋 ${t('topBar.greeting', 'Hi {{username}}!', { username })}`
              : `👋 ${t('topBar.greetingNoName', 'Hi!')}`}
          </Typography>
        </Box>
        <Box sx={[...topBarIconBoxSx, { mr: 1 }]}>
          <Avatar name={username} />
        </Box>
        <Box sx={{ flexGrow: 0 }}>
          <Tooltip title={t('topBar.openSettings', 'Open settings')} placement="bottom-start">
            <IconButton onClick={handleOpenRightDrawer} sx={topBarIconBoxSx}>
              <SettingsOutlinedIcon
                sx={(theme) => ({
                  color: theme.vars?.palette.satosRed[500],
                  fontSize: theme.fontSizes.f20,
                  opacity: 0.7,
                })}
              />
            </IconButton>
          </Tooltip>
          <RightDrawer open={Boolean(anchorElUser)} onClose={handleCloseRightDrawer} />
        </Box>
      </Toolbar>
    </StyledAppBar>
  );
};
